import { Currency } from 'core/localization/currency';
import { IMessagingClient } from '@agoda/messaging-client';
import { PerformanceClient } from '@agoda/performance-client';
import { WebAnalyticsModule } from '@agoda/analytics-data-acquisition';
import { PlaceType } from 'core/type/Places.types';
import { ActivitiesBehavior } from 'core/type/behaviors.types';
import { CommonHeaderProps, CurrencyApiResponse, LanguageApiResponse } from 'core/type/header.types';
import { TrafficTypeCronos } from 'core-framework/constant/Traffic.types';
import { LoginParams, UserNotificationModel } from '@agoda/layout/lib/framework/model/layout';
import { ExperimentId } from 'core/type/ExperimentId';
import { TopDestination, TopDestinationsGroup } from 'core/type/ActivitiesTopDestinations.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { ActivitiesPageTypes } from 'core/constant/analytics/AnalyticsPageType';

export type Experiments = Partial<Record<ExperimentId, boolean>>;
export type CmsValue = Record<number, string>;
export type IdentityTokens = Record<string, string>;

export interface CmsProps {
    cms: CmsValue;
}

export interface Language {
    cultureCode: string;
    isRtl: boolean;
    languageId: number;
    languageGroupId: number;
    locale: string;
    textDirection: 'rtl' | 'ltr';
}

export interface FeMessagingConfig {
    mode: string;
    cluster: string;
    dc: string;
    pageTypeId: number;
    disable: boolean;
    environment?: string;
    userId?: string;
    machineName?: string;
    sessionId?: string;
}

// Please keep variables sorted
export interface BootstrapConfig {
    behavior: ActivitiesBehavior;
    browser: BrowserInfo;
    cartGatewayUrl: string;
    cms: CmsValue;
    currency: Currency;
    experiments: Experiments;
    feMessagingConfig: FeMessagingConfig;
    gatewayUrl: string;
    geo?: Geo;
    language: Language;
    pageConfig: PageConfig;
    partner: PartnerDetail;
    requestProfile?: RequestProfile;
    supportedLocalization: SupportedLocalization;
    topSubcategories?: TopSubcategory[];
    whiteLabelConfig: WhiteLabelConfig;
    userDevice: UserDeviceModel;
    analyticsSessionId: string;
    assetPath: string;
    cdn: string;
    correlationId: string;
    isBot: boolean;
    isChinaIcp: boolean;
    isDebug: boolean;
    isInternal: boolean;
    isLoggedIn: boolean;
    isMockData: boolean;
    isWebView: boolean;
    isUseAapiMock: boolean;
    memberId: number;
    rootUrl: string;
    sessionId: string;
    webgateUrl: string;
    webViewFeatureToggles: string[];
    whiteLabelId: number;
    userId: string;
    userVariant?: string;
    isPrerender: boolean;
    version: string;
    identityTokens: IdentityTokens;
    multiProductMse: MultiProductMse;
}

export interface MultiProductMse {
    cookieValue: string;
}

export interface PartnerDetail {
    loyaltyPartnerClaim: string;
    ssoUrl: string;
    ssoLogoutUrl: string;
}

export interface SupportedLocalization {
    // All values in Lower Case
    languages: string[];
}

export interface RequestProfile {
    isBot?: boolean;
    severityScore?: number;
    botPriority?: number;
    ttl?: Date;
    pageCost?: number;
    canRedirect?: boolean;
    knownBotName?: string;
    styxRule?: number;
    trafficType?: TrafficTypeCronos;
}

export interface UserDeviceModel {
    platformId: number; // UNKNOWN = 0,DESKTOP = 1, TABLET = 2, PHONE = 4, MOBILEAPP = 1007
    devicePlatformId: number; // UNKNOWN = 0,DESKTOP = 1, TABLET = 2, PHONE = 4,
    deviceType: string; // tablet, mobile, desktop
    mobileDeviceType: MobileDeviceType; // NOTMOBILE, ANDROID, IOS, WINDOWSPHONE
}

export enum MobileDeviceType {
    NotMobile = 'NOTMOBILE',
    Android = 'ANDROID',
    iOS = 'IOS',
    WindowsPhone = 'WINDOWSPHONE',
}

export interface WhiteLabelConfig {
    bookingFormUrl: string;
    defaultCurrency: string;
    defaultLanguage: string;
    name: string;
    theme: WebTheme;
    whiteLabelId: number;
    whiteLabelKey: string;
    homeImageUrl: string;
}

export interface WebTheme {
    cssRootName: string;
    logo: string;
    favicon: string;
}

export interface AirportCode {
    code: string;
    type: string;
}

export interface DisplayNames {
    name: string;
    geoHierarchyName: string;
    categoryName: string;
}

export enum CityContentImageSize {
    Desktop = 'desktop',
    Mobile = 'mobile',
}

export interface CityContent {
    description: string;
    imageUrls: Record<CityContentImageSize, string>;
}

export interface Geo {
    areaId: number;
    areaName: string;
    areaNameTranslated: string;
    cityId: number;
    cityContent: CityContent;
    cityName: string;
    cityNameTranslated: string;
    cityAbbr: string;
    countryId: number;
    countryName: string;
    countryNameTranslated: string;
    countryISO2: string;
    addressId: number;
    activityId: number;
    activityName: string;
    activityNameTranslated: string;
    stateId: number;
    stateName: string;
    stateNameTranslated: string;
    id: number;
    isAddress: boolean;
    isBestSeller: boolean;
    isPopular: boolean;
    isSubSuggestion: boolean;
    name: string;
    nameTranslated: string;
    placeType: PlaceType;
    airportCodes: AirportCode[];
    displayNames: DisplayNames;
    objectTypeId: number;
    latitude: number;
    longitude: number;
    url: string;
    whiteLabelToken: string;
    type: TextSearchType;
    topDestinations: TopDestination[];
    topDestinationCityId: number;
}

export interface PageConfig {
    aid: number;
    cid: number;
    cttp: number;
    cultureInfoName: string;
    currencyCode: string;
    currencyId: number;
    htmlLanguage: string;
    isRealUser: true;
    languageId: number;
    loginLvl: 0 | 1 | 2;
    machineName: string;
    mode: string;
    origin: string;
    pageTypeId: ActivitiesPageTypes;
    realLanguageId: number;
    sessionId: string;
    stateCode: string;
    storefrontId: number;
    tag: string;
    trafficGroupId: number;
    trafficSubGroupId: number;
    useFullPageLogin: true;
    userId: string;
    whitelabelid: number;
}

export interface BrowserInfo {
    browserName: string;
    family: string;
    major: string;
    isDesktop: boolean;
    isEdge: boolean;
    isIE: boolean;
    isMobile: boolean;
    isTablet: boolean;
    isWebView: boolean;
}

export interface RiskifiedBeacon {
    pageId: string;
    shop: string;
    go: (path: string) => void;
}

export interface Link {
    text: string;
    link: string;
    id?: string | number;
    rel?: string;
    target?: string;
    data?: Record<string, string>;
}

export interface BrandingProps {
    brands: string[];
    updateFooterLogos?: boolean;
}

export interface LinkGroup {
    title: string;
    dataSelenium?: string;
    className?: string;
    list: Link[];
}

export interface CopyRights {
    machineName: string;
    material: string;
    priceline: string;
    showMachineNameBelow: boolean;
    travel: string;
    brands: string[];
    updateFooterLogos: boolean;
}

export interface FooterProps {
    params: FooterOwnProps;
}

export interface FooterOwnProps {
    linksGroups: LinkGroup[];
    copyrights: CopyRights;
    isFooterLogoLinksList?: boolean;
    isHorizontalFooterLinkLayout?: boolean;
    isNoTitleLinksGroup?: boolean;
}

export enum CartWebViewBridgeEnum {
    openCartBFWebView = 'openCartBFWebView',
    getFeatureToggles = 'getFeatureToggles',
    openCartPage = 'openCartPage',
    openPrevNavStack = 'openPrevNavStack',
    openShareSheet = 'openShareSheet',
    openShareSheetV2 = 'openShareSheetV2',
}

export enum AppFeaturesToggleEnum {
    EnableOpenCartBFWebViewHook = 'EnableOpenCartBFWebViewHook',
    EnableActivitiesInCart = 'EnableActivitiesInCart',
    EnableActivitiesWebViewHeader = 'EnableActivitiesWebViewHeader',
    EnableActivitiesDetailShare = 'EnableActivitiesDetailShare',
    EnableActivitiesDetailShareV2 = 'EnableActivitiesDetailShareV2',
}

export interface CartWebViewBridgeAddEvents {
    // openCartBFWebView is for opening cart BF WebView when user click to go to Cart BF1 from the activity detail page
    [CartWebViewBridgeEnum.openCartBFWebView]: (url: string) => void;
    [CartWebViewBridgeEnum.openCartPage]: (value?: string) => void;
    [CartWebViewBridgeEnum.openPrevNavStack]: (value?: string) => void;
    [CartWebViewBridgeEnum.openShareSheet]: (params: WebViewQuickShareParameters) => void;
    [CartWebViewBridgeEnum.openShareSheetV2]: (url: string, message: string, title: string) => void;
}

export interface WebViewQuickShareParameters {
    url: string;
    message: string;
    title: string;
}

export interface CartWebViewBridgeListenEvents {
    // getFeatureToggles is for getting feature toggles from native app
    [CartWebViewBridgeEnum.getFeatureToggles]: (value?: string) => Promise<string>;
}

export interface WebKitMessageHandlers<T> {
    messageHandlers?: {
        [K in keyof T]: {
            postMessage: T[K];
        };
    };
}

export interface TopSubcategory {
    subcategoryId: string;
    rankInCity: number;
    activityCount: number;
}

export type CartWebViewBridge = CartWebViewBridgeAddEvents & CartWebViewBridgeListenEvents;

declare global {
    // Global variables set from webpack
    let VERSION: string;
    let USE_MOCK_DATA: boolean;
    const PAYPAL_CLIENT_ID: string;
    const AFTER_PAY_SDK_SRC: string;

    interface Window {
        bootstrapConfig: BootstrapConfig;
        header: CommonHeaderProps;
        footer: FooterProps;
        webAnalytics: WebAnalyticsModule;
        performanceClient: PerformanceClient;
        isJestTest?: boolean;
        feMessaging: IMessagingClient;
        RISKX: RiskifiedBeacon;
        riskified_beacon_enabled: boolean;
        cartGwUrl?: string;
        gatewayApiUrl?: string;
        webgateApiUrl?: string;
        forcedActivitiesGqlApiUrl?: string;
        forcedVehicleGqlApiUrl?: string;
        mocks: {
            currencies: CurrencyApiResponse;
            languages: LanguageApiResponse;
            layoutLoginParams: { body: LoginParams };
            userNotificationModel: UserNotificationModel;
            topDestinationsGroup: TopDestinationsGroup;
        };
        // webkit will be set by MMB WebView in IOS
        webkit?: WebKitMessageHandlers<CartWebViewBridge>;
        // ActivitiesInterface will be set by MMB WebView in Android
        ActivitiesInterface?: CartWebViewBridge;

        attachEvent: (event: string, listener: EventListener) => boolean;
    }
}
